import React from 'react';
import { trpc } from '@/utils/trpc-client';
import { Card, CardContent } from '@/components/forms/ui/card';
import { Button } from '@/components/forms/ui/button';
import { format } from 'date-fns';
import clsx from 'clsx';
import { adjustForCPMById } from '@/features/line-items/line-item-form/utils/line-item-form-utils';

export function RateCardHistory() {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    trpc.rateCardHistory.useInfiniteQuery(
      {},
      {
        getNextPageParam: lastPage => lastPage.nextCursor
      }
    );

  if (isLoading) {
    return <div className="text-muted-foreground">Loading history...</div>;
  }

  const entries = data?.pages.flatMap(page => page.entries) ?? [];

  if (entries.length === 0) {
    return <div className="text-muted-foreground">No history entries found.</div>;
  }

  return (
    <div className="space-y-4">
      {entries.map(entry => (
        <Card key={entry.changesetId}>
          <CardContent className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {format(new Date(entry.timestamp), 'MMM d, yyyy h:mm a')}
              </div>
              <div className="text-sm font-medium">{entry.userName ?? 'System'}</div>
            </div>
            <div className="space-y-2">
              {entry.changes.map((change, idx) => (
                <ChangeItem key={idx} change={change} />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            variant="outline"
            size="sm">
            {isFetchingNextPage ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}
    </div>
  );
}

type RateChange = {
  type: 'rate_change';
  tacticId: number;
  tacticName: string;
  channelName: string;
  unitPriceTypeName: string;
  unitPriceTypeId: number;
  targetMargin: number;
  unitPrice: number;
};

type PlatformChange = {
  type: 'platform_change';
  tacticId: number;
  tacticName: string;
  channelName: string;
  platformName: string;
  action: 'added' | 'removed';
};

type Change = RateChange | PlatformChange;

function ChangeItem({ change }: { change: Change }) {
  if (change.type === 'rate_change') {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span
          className={clsx('rounded px-2 py-0.5 text-xs font-medium', 'bg-blue-100 text-blue-700')}>
          Rate
        </span>
        <span className="text-muted-foreground">
          {change.channelName} / {change.tacticName} ({change.unitPriceTypeName})
        </span>
        <span className="font-medium">
          ${adjustForCPMById(change.unitPrice, change.unitPriceTypeId)} @{' '}
          {change.targetMargin * 100}%
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-sm">
      <span
        className={clsx(
          'rounded px-2 py-0.5 text-xs font-medium',
          change.action === 'added' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        )}>
        {change.action === 'added' ? 'Added' : 'Removed'}
      </span>
      <span className="text-muted-foreground">
        {change.channelName} / {change.tacticName}
      </span>
      <span className="font-medium">{change.platformName}</span>
    </div>
  );
}
